// src/pages/vendors/purchase-order-form-components/POHeaderSection.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Assignment as AssignmentIcon,
  Print as PrintIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const POHeaderSection = ({ purchaseOrder, isEdit, companyName, onBack, onPreview, onSave, saving, canEdit = true }) => {
  const theme = useTheme();

  return (
    <Card sx={{
      borderRadius: 3,
      mb: 3,
      mt: 5,
      border: `1px solid ${theme.palette.grey[400]}`,
      background: theme.palette.grey[100],
      backdropFilter: 'blur(20px)',
      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
      },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
        opacity: 0.8
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backdropFilter: 'blur(10px)',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'primary.main', 
                mb: 0.5,
                fontSize: '2rem',
                letterSpacing: '-0.3px'
              }}>
                {isEdit ? `Edit Purchase Order #${purchaseOrder.vendor_PO_number}` : 'New Purchase Order'}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                fontWeight: 500,
                fontSize: '1rem'
              }}>
                {companyName || 'Loading...'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {canEdit && onSave && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={onSave}
                disabled={saving}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.35)}`
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.primary.main, 0.5),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {saving ? 'Saving...' : 'Update Purchase Order'}
              </Button>
            )}
            <Button
              variant="outlined" 
              startIcon={<PrintIcon />} 
              onClick={onPreview}
              sx={{ 
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '0.9rem',
                px: 3,
                py: 1.5,
                background: 'white',
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateY(-2px)',
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                },
                transition: 'all 0.3s ease'
              }}
            >
              Preview
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ 
          color: 'text.secondary', 
          maxWidth: 700,
          lineHeight: 1.6,
          fontSize: '1.05rem',
          fontWeight: 500,
          mt: 2
        }}>
          {isEdit ? 'Update purchase order details and line items for your supplier.' : 'Create a new purchase order to request inventory items from suppliers.'}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default POHeaderSection;